/** @jsx jsx */
import { Email3d } from '@datacamp/waffles/asset';
import { Dialog } from '@datacamp/waffles/dialog';
import { Notification } from '@datacamp/waffles/notification';
import { Paragraph } from '@datacamp/waffles/paragraph';
import { tokens } from '@datacamp/waffles/tokens';
import { jsx } from '@emotion/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { State } from '../../interfaces/State';
import * as actions from '../../redux/actions';
import * as selectors from '../../redux/selectors';

import MobilePopupIllustration from './MobilePopupIllustration';

type StateProps = {
  failed: boolean;
  sending: boolean;
  sent: boolean;
  userId: number;
};

type DispatchProps = {
  onSendLink: () => void;
};

type MobilePopupModalProps = StateProps & DispatchProps;

type SendButtonProps = MobilePopupModalProps & { children: React.ReactNode };

const SendButton: React.FC<SendButtonProps> = ({
  children,
  failed,
  onSendLink,
  sending,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <Dialog.Button
        variant="primary"
        isLoading={sending}
        onClick={onSendLink}
        data-cy="mobile-popup-send-button"
      >
        {children}
      </Dialog.Button>
      {failed && (
        <Notification
          title={t('MobilePopupModal.failedMessage')}
          variant="error"
          css={{
            marginTop: tokens.spacing.medium,
          }}
        />
      )}
    </div>
  );
};

const MobilePopupModalInternal: React.FC<MobilePopupModalProps> = (props) => {
  const { t } = useTranslation();

  if (props.sent) {
    return (
      <>
        <Dialog.Header>{t('MobilePopupModal.sentHeader')}</Dialog.Header>
        <Dialog.Body data-cy="mobile-popup">
          <Paragraph>{t('MobilePopupModal.sentText')}</Paragraph>
          <div css={{ color: tokens.colors.navy, marginTop: -40 }}>
            <Email3d width="200" />
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          <SendButton {...props}>{t('MobilePopupModal.sentButton')}</SendButton>
        </Dialog.Footer>
      </>
    );
  }

  return (
    <>
      <Dialog.Header>{t('MobilePopupModal.header')}</Dialog.Header>
      <Dialog.Body data-cy="mobile-popup">
        <Paragraph>{t('MobilePopupModal.text')}</Paragraph>
        <div>
          <MobilePopupIllustration />
        </div>
      </Dialog.Body>
      <Dialog.Footer>
        <SendButton {...props}>{t('MobilePopupModal.button')}</SendButton>
      </Dialog.Footer>
    </>
  );
};

function mapStateToProps(state: State): StateProps {
  return {
    userId: selectors.selectUserSettings(state).get('id'),
    sending: selectors.selectIsMobilePopupLinkSending(state),
    sent: selectors.selectIsMobilePopupLinkSent(state),
    failed: selectors.selectIsMobilePopupLinkFailed(state),
  };
}

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  onSendLink: () => {
    dispatch(actions.sendMobilePopupLink());
  },
});

const MobilePopupModal = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MobilePopupModalInternal);

export default MobilePopupModal;
